import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Heading } from '../../components';
import { richText } from '../../util/richText';
import { isAllowedEmbedSrc } from '../../util/allowedLinks';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

// Check if text contains HTML from rich text editor
const containsHtml = text => {
  if (!text || typeof text !== 'string') return false;
  // Check for regular HTML tags
  if (/<[a-z][\s\S]*>/i.test(text)) return true;
  // Check for HTML-encoded tags (e.g., &lt;p&gt;)
  if (/&lt;[a-z][\s\S]*&gt;/i.test(text)) return true;
  // Check for Lexical editor class names (in case brackets are encoded differently)
  if (/class="editor-/i.test(text)) return true;
  // Check for video embed classes
  if (/class="video-embed/i.test(text)) return true;
  return false;
};

// Decode HTML entities and fix any encoding issues (in the browser only)
const decodeHtmlEntities = text => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  // Also handle full-width angle brackets (sometimes used by systems)
  return textarea.value.replace(/\uff1c/g, '<').replace(/\uff1e/g, '>');
};

// Players may only load from the video and audio hosts listings allow (util/allowedLinks.js)
let embedHookAdded = false;
const addEmbedHook = () => {
  if (embedHookAdded) return;
  embedHookAdded = true;
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'iframe' && !isAllowedEmbedSrc(node.getAttribute('src'))) {
      node.parentNode?.removeChild(node);
    }
  });
};

// The seller's HTML, cleaned (in the browser only: DOMPurify needs a DOM)
const sanitizeHtml = html => {
  addEmbedHook();
  return DOMPurify.sanitize(decodeHtmlEntities(html), {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'pre',
      'code',
      'span',
      'div',
      'img',
      'iframe',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'class',
      'src',
      'alt',
      'width',
      'height',
      // iframe attributes for YouTube/Vimeo embeds
      'frameborder',
      'allowfullscreen',
      'allow',
      'title',
      'loading',
      'referrerpolicy',
      // style for video containers
      'style',
    ],
    ALLOW_DATA_ATTR: false,
    ADD_URI_SAFE_ATTR: ['src'],
  });
};

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/**
 * The seller's HTML as plain text: what the server renders (as text, which React escapes,
 * so nothing in it can run) and the browser's first render, until the cleaned HTML replaces it.
 */
export const plainTextOf = html =>
  String(html)
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|li|h[1-6]|blockquote|div)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, name) => ENTITIES[name])
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const SectionTextMaybe = props => {
  const { text, htmlText, heading, showAsIngress = false } = props;
  const textClass = showAsIngress ? css.ingress : css.text;

  // Prefer htmlText (from publicData.descriptionHtml) if available
  // Fall back to text and check if it contains HTML
  const displayText = htmlText || text;

  // If we have explicit htmlText, always treat it as HTML
  // Otherwise, check if text contains HTML (from rich text editor)
  const isHtmlContent = htmlText ? true : containsHtml(displayText);

  // The cleaned HTML replaces the plain text once the page runs in the browser
  const [inBrowser, setInBrowser] = useState(false);
  useEffect(() => setInBrowser(true), []);
  const cleanHtml = isHtmlContent && inBrowser ? sanitizeHtml(displayText) : null;

  // For HTML content, render sanitized HTML; for plain text, use richText utility
  const content = isHtmlContent
    ? null
    : richText(displayText, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
        breakChars: '/',
      });

  return displayText ? (
    <div className={css.sectionText}>
      {heading ? (
        <Heading as="h2" rootClassName={css.sectionHeading}>
          {heading}
        </Heading>
      ) : null}
      {cleanHtml !== null ? (
        <div className={textClass} dangerouslySetInnerHTML={{ __html: cleanHtml }} />
      ) : isHtmlContent ? (
        <p className={`${textClass} ${css.preLine}`}>{plainTextOf(displayText)}</p>
      ) : (
        <p className={`${textClass} ${css.preLine}`}>{content}</p>
      )}
    </div>
  ) : null;
};

export default SectionTextMaybe;
